import type { ChatMessage } from "./types";
import { extractJsonFromText } from "./utils";

/** Коды дневного лимита демо (t_4c03e049): клиент показывает модалку или текст. */
export const DEMO_LIMIT_CODES = new Set(["demo_limit_anon", "demo_limit_user"]);

/** Достаёт текст ошибки из ответа сервера (не бросает). */
async function extractErrorMessage(response: Response): Promise<string> {
	try {
		const body = await response.json();
		if (body && typeof body.message === "string" && body.message.trim()) {
			return body.message;
		}
		if (body && typeof body.error === "string" && body.error.trim()) {
			return body.error;
		}
	} catch {
		// тело не JSON — показываем только статус
	}
	return `HTTP ${response.status}`;
}

/** Код ошибки из тела ответа (нужен для лимитов демо). */
async function extractErrorCode(response: Response): Promise<string | null> {
	try {
		const body = await response.json();
		if (body && typeof body.error === "string") {
			return body.error;
		}
	} catch {
		// тело не JSON — кода нет
	}
	return null;
}

export class AIBuilderService {
	static async sendMessage(
		messages: ChatMessage[],
		sessionId: string,
		onStream: (content: string) => void,
		onError: (error: string) => void,
		file?: File | null,
		options?: { demo?: boolean }
	): Promise<{ fullText: string; foundJson: any }> {
		const demoHeaders: Record<string, string> = options?.demo
			? { "X-AI-Builder-Mode": "demo" }
			: {};
		try {
			// Документ отправляем multipart-ом: текст извлекается на сервере
			// (pdf-parse / mammoth / xlsx / OCR), в браузере этих библиотек нет.
			let response: Response;
			if (file) {
				const formData = new FormData();
				formData.append("file", file);
				formData.append("sessionId", sessionId);
				formData.append("messages", JSON.stringify(messages));
				response = await fetch("/api/ai-builder", {
					method: "POST",
					headers: demoHeaders,
					body: formData,
				});
			} else {
				response = await fetch("/api/ai-builder", {
					method: "POST",
					headers: {
						"Content-Type": "application/json",
						...demoHeaders,
					},
					body: JSON.stringify({ messages, sessionId }),
				});
			}

			// Ответ-ошибку (400/401/403/429/5xx) НЕЛЬЗЯ отдавать в чат как ответ модели:
			// её JSON-тело уходит в extractJsonFromText → normalizeAiSchema и в предпросмотр
			// попадает «схема» с нулём полей. Показываем сообщение сервера как ошибку.
			if (!response.ok) {
				// Дневной лимит демо отдаём кодом: вызывающая сторона решает,
				// показать модалку регистрации или текст (сообщение сервера англоязычное).
				if (response.status === 429) {
					const code = await extractErrorCode(response);
					if (code && DEMO_LIMIT_CODES.has(code)) {
						onError(code);
						return { fullText: "", foundJson: null };
					}
				}
				onError(await extractErrorMessage(response));
				return { fullText: "", foundJson: null };
			}

			if (!response.body) {
				onError("no_response");
				return { fullText: "", foundJson: null };
			}

			const reader = response.body.getReader();
			let fullText = "";
			let foundJson = null;

			while (true) {
				const { value, done } = await reader.read();
				if (done) break;

				const chunk = new TextDecoder().decode(value);
				fullText += chunk;
				onStream(fullText);

				if (!foundJson) {
					foundJson = extractJsonFromText(fullText);
				}
			}

			return { fullText, foundJson };
		} catch (error) {
			onError("send_failed");
			return { fullText: "", foundJson: null };
		}
	}
}
